import React from 'react';
import { aggregateFromGradeCode, computePleAggregateTotal, resolvePleDivision } from './aggregateUtils';

export default function ProgressTable({ report, showClassPosition = true, showStreamPosition = true, showDivision = true, showAggregate = true, showTotalAggregate = true, showMarks = true, showGrade = true, showSubjectComments = true }) {
  const subjects = report?.subjects_data || [];

  // Dynamically detect assessment types from subjects_data
  const assessmentCols = React.useMemo(() => {
    const map = new Map();
    subjects.forEach((sub) => {
      (sub.papers || []).forEach((paper) => {
        (paper.assessments || []).forEach((a) => {
          if (a.include_in_final === false || a.policy_included === false) return;
          const code = a.assessment_type?.assessment_code || a.assessment_code;
          if (code && !map.has(code)) {
            map.set(code, { code, max_marks: a.max_marks, weight: a.weight_percentage });
          }
        });
      });
    });
    const caKeywords = /^(bot|mot|ca|mid|cont|test|quiz|hw|home)/i;
    const eotKeywords = /^(eot|exam|end|final|term)/i;
    const order = (code) => {
      if (caKeywords.test(code)) return 0;
      if (eotKeywords.test(code)) return 2;
      return 1;
    };
    return Array.from(map.values()).sort((a, b) => order(a.code) - order(b.code));
  }, [subjects]);

  const showPeriodCols = showMarks || showGrade;
  const subColCount = (showMarks ? 1 : 0) + (showGrade ? 1 : 0);
  const hasTwoHeaderRows = showPeriodCols;

  return (
    <>
      <p className="rc-section-title">Academic Progress</p>
      <table className="rc-table">
        <thead>
          <tr>
            <th className="left" rowSpan={hasTwoHeaderRows ? 2 : 1} style={{ width: '15%' }}>Subject</th>
            {showPeriodCols && assessmentCols.map((col) => (
              <th key={col.code} colSpan={subColCount} style={{ textAlign: 'center', width: '10%' }}>{col.code}</th>
            ))}
            <th rowSpan={hasTwoHeaderRows ? 2 : 1} style={{ width: '30%' }} className="left">Remarks</th>
            <th rowSpan={hasTwoHeaderRows ? 2 : 1} style={{ width: '4%', textAlign: 'center' }}>Initials</th>
          </tr>
          {hasTwoHeaderRows && (
            <tr>
              {assessmentCols.map((col) => (
                <React.Fragment key={col.code}>
                  {showMarks && <th style={{ textAlign: 'center', fontSize: '7pt', fontWeight: 'normal', width: '5%' }}>Marks</th>}
                  {showGrade && <th style={{ textAlign: 'center', fontSize: '7pt', fontWeight: 'normal', width: '5%' }}>Agg</th>}
                </React.Fragment>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {subjects.length === 0 ? (
            <tr><td colSpan={1 + (showPeriodCols ? assessmentCols.length * subColCount : 0) + 2} style={{ textAlign: 'center', color: '#888', padding: 10 }}>No subject data</td></tr>
          ) : subjects.map((sub, si) => {
            const subjectName = sub.subject?.subject_name || sub.subject?.name || `Subject ${si + 1}`;
            const remark = sub.subject_comment || sub.teacher_comment || sub.subject_remarks || '';

            const markByCode = {};
            let teacherInitials = null;
            (sub.papers || []).forEach((paper) => {
              if (!teacherInitials && paper.teacher_initials) teacherInitials = paper.teacher_initials;
              (paper.assessments || []).forEach((a) => {
                const code = a.assessment_type?.assessment_code || a.assessment_code;
                if (code) markByCode[code] = a;
              });
            });

            return (
              <tr key={sub.subject?.id || si}>
                <td className="subject-name">{subjectName}</td>
                {showPeriodCols && assessmentCols.map((col) => {
                  const a = markByCode[col.code];
                  const mark = a ? (
                    a.is_absent ? 'ABS' :
                    a.is_exempted ? 'EXM' :
                    (a.score ?? a.marks_obtained ?? a.raw_score) != null ? Number(a.score ?? a.marks_obtained ?? a.raw_score).toFixed(0) : '-'
                  ) : '-';
                  const grade = a?.assessment_grade || '-';
                  
                  return (
                    <React.Fragment key={col.code}>
                      {showMarks && <td style={{ textAlign: 'center', fontSize: '8.5pt' }}>{mark}</td>}
                      {showGrade && <td style={{ textAlign: 'center', fontSize: '8.5pt', fontWeight: 'bold' }}>{grade}</td>}
                    </React.Fragment>
                  );
                })}
                <td style={{ fontSize: '8.5pt', fontStyle: 'italic', color: '#444' }}>{remark}</td>
                <td style={{ textAlign: 'center', fontSize: '8.5pt', fontWeight: 'bold' }}>{teacherInitials || ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Table 3: Summary */}
      {(() => {
        const aggregateTotal = report?.aggregate_total ?? computePleAggregateTotal(report?.subjects_data, report?.subjects_counted ?? 4);
        const division = report?.division || resolvePleDivision(aggregateTotal);
        const hasClassPos = showClassPosition && report?.class_position != null;
        const hasStreamPos = showStreamPosition && report?.stream_position != null;
        const hasAggregates = showTotalAggregate && aggregateTotal != null;
        const hasDivision = showTotalAggregate && showDivision && division != null;

        if (!hasClassPos && !hasStreamPos && !hasAggregates && !hasDivision) return null;

        return (
          <>
            <p className="rc-section-title">Summary</p>
            <table className="rc-table" style={{ marginBottom: 8 }}>
              <tbody>
                {(hasClassPos || hasStreamPos) && (
                  <tr>
                    {hasClassPos && (
                      <>
                        <th className="rc-bio-label" style={{ width: '20%' }}>Position in Class</th>
                        <td className="rc-bio-value" style={{ width: '30%' }}>
                          {report.class_position} / {report.class_total_students ?? '-'}
                        </td>
                      </>
                    )}
                    {hasStreamPos && (
                      <>
                        <th className="rc-bio-label" style={{ width: '20%' }}>Position in Stream</th>
                        <td className="rc-bio-value" style={{ width: '30%' }}>
                          {report.stream_position} / {report.stream_total_students ?? '-'}
                        </td>
                      </>
                    )}
                  </tr>
                )}
                {(hasAggregates || hasDivision) && (
                  <tr>
                    {hasAggregates && (
                      <>
                        <th className="rc-bio-label">Aggregate</th>
                        <td className="rc-bio-value">{aggregateTotal}</td>
                      </>
                    )}
                    {hasDivision && (
                      <>
                        <th className="rc-bio-label">Division</th>
                        <td className="rc-bio-value">{division}</td>
                      </>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </>
        );
      })()}
    </>
  );
}
